import { Avatar, Badge, Button, Card, Flex, Text } from '@radix-ui/themes'
import { CSSProperties, ReactNode } from 'react'
import { NavLink } from 'react-router-dom'
import {
  BackpackIcon,
  BarChartIcon,
  Building2Icon,
  BuildingIcon,
  CarIcon,
  ChevronRightIcon,
  CircleDollarSignIcon,
  FileCheck2Icon,
  FileClockIcon,
  FileMinus2Icon,
  FilePlus2Icon,
  FileSearchIcon,
  FileTextIcon,
  HandshakeIcon,
  HomeIcon,
  KeyRoundIcon,
  MessagesSquareIcon,
  ArrowLeftRightIcon,
  Share2Icon,
  StoreIcon,
  UserCogIcon,
  UsersIcon,
  SearchIcon,
} from 'lucide-react'

import { UserProfileRole } from '@/modules/users/types/UserProfile'
import { stringToNamedColor } from '@/utils/stringToNamedColor'
import { AppPath } from '@/enums/AppPath'
import { useAuth } from '@/hooks/useAuth'

interface MenuItem {
  icon: ReactNode
  label: string
  show: boolean
  to: string
}

export const AppMenu = () => {
  const { userData, hasRole, hasAnyRole, unreadConsultasCount, unreadMessagesCount, unreadDealsCount } = useAuth()

  // Organization-based control: Only checks organization setting for Market module
  const marketModuleEnabled = userData?.organization?.external?.autofin?.enabled ?? false
  // Restriction: Only Veekls Demo organization can use Transfers feature
  const transfersEnabled = ['5a628323cdb9dd31b15b85a8', '66b51a545e7ba00ea85cc5b6'].includes(
    userData?.organization?._id || ''
  )

  const links: MenuItem[] = [
    {
      label: 'Inicio',
      icon: <HomeIcon />,
      show: true,
      to: '/',
    },
    {
      label: 'Reportes',
      show: hasAnyRole(UserProfileRole.DealershipReports, UserProfileRole.DealershipStockReport),
      to: `/${AppPath.Reports}`,
      icon: <BarChartIcon />,
    },
    {
      label: 'Consulta Patente',
      show: true,
      to: `/${AppPath.Patente}`,
      icon: <SearchIcon />,
    },
    {
      label: 'Control de ventas',
      show: hasRole(UserProfileRole.ControlSaleNotes),
      to: `/${AppPath.Sales}`,
      icon: <BackpackIcon />,
    },
    {
      label: 'Mensajes',
      show: hasRole(UserProfileRole.AllMessages),
      to: `/${AppPath.Messages}`,
      icon: <MessagesSquareIcon />,
    },
    {
      label: 'Negocios',
      show: hasAnyRole(UserProfileRole.AllDeals, UserProfileRole.OwnDeals),
      to: `/${AppPath.Deals}`,
      icon: <HandshakeIcon />,
    },
    {
      label: 'Consignaciones',
      show: hasRole(UserProfileRole.EditConsignments),
      to: `/${AppPath.Consignments}`,
      icon: <KeyRoundIcon />,
    },
    {
      label: 'Cotizaciones',
      show: hasAnyRole(UserProfileRole.AllEstimates, UserProfileRole.OwnEstimates),
      to: `/${AppPath.Estimates}`,
      icon: <FileSearchIcon />,
    },
    {
      label: 'Notas de venta',
      show: hasRole(UserProfileRole.EditSaleNotes),
      to: `/${AppPath.SaleNotes}`,
      icon: <FileCheck2Icon />,
    },
    {
      label: 'Compras',
      show: hasRole(UserProfileRole.EditPurchases),
      to: `/${AppPath.Purchases}`,
      icon: <FilePlus2Icon />,
    },
    {
      label: 'Transferencias',
      show:
        transfersEnabled &&
        hasAnyRole(UserProfileRole.EditTransfers, UserProfileRole.ViewTransfers, UserProfileRole.EditSaleNotes),
      to: `/${AppPath.Transfers}`,
      icon: <ArrowLeftRightIcon />,
    },
    {
      label: 'Liquidaciones',
      show: hasRole(UserProfileRole.EditSettlements),
      to: `/${AppPath.Settlements}`,
      icon: <FileMinus2Icon />,
    },
    {
      label: 'Reservas',
      show: hasAnyRole(UserProfileRole.AllReservations, UserProfileRole.OwnReservations),
      to: `/${AppPath.Reservations}`,
      icon: <FileClockIcon />,
    },
    {
      label: 'Vehículos',
      show: hasRole(UserProfileRole.ViewVehicles),
      to: `/${AppPath.Vehicles}`,
      icon: <CarIcon />,
    },
    {
      label: 'Market',
      show: marketModuleEnabled,
      to: '/market',
      icon: <StoreIcon />,
    },
    {
      label: 'Publicaciones',
      to: `/${AppPath.Publish}`,
      show: hasRole(UserProfileRole.ViewVehicles),
      icon: <Share2Icon />,
    },
    {
      label: 'Clientes',
      to: `/${AppPath.Clients}`,
      show: hasRole(UserProfileRole.ViewClients),
      icon: <UsersIcon />,
    },
    {
      label: 'Créditos',
      show: hasAnyRole(UserProfileRole.AllDeals, UserProfileRole.OwnDeals),
      to: `/${AppPath.Loans}`,
      icon: <CircleDollarSignIcon />,
    },
    {
      to: `/${AppPath.Users}`,
      label: 'Usuarios',
      show: hasRole(UserProfileRole.ViewProfiles),
      icon: <UserCogIcon />,
    },
    {
      label: 'Plantillas',
      show: hasRole(UserProfileRole.EditDocumentTemplates),
      to: `/${AppPath.Templates}`,
      icon: <FileTextIcon />,
    },
    {
      to: `/${AppPath.Branches}`,
      label: 'Sucursales',
      show: hasRole(UserProfileRole.AllBranches),
      icon: <Building2Icon />,
    },
    {
      label: 'Automotora',
      show: hasRole(UserProfileRole.EditSaleNotes),
      to: `/${AppPath.Dealership}`,
      icon: <BuildingIcon />,
    },
  ]

  return (
    <Flex direction="column" flexGrow="0" flexShrink="0" flexBasis="0" gap="4" className="print:hidden">
      <Card>
        <Flex gap="2" align="center">
          <Flex align="end" overflow="hidden">
            <Avatar
              color={stringToNamedColor(userData?.user?.fullname ?? '')}
              fallback={userData?.user?.firstname?.[0] ?? '?'}
              radius="full"
              size="2"
            />
            <Avatar
              color={stringToNamedColor(userData?.organization?.name ?? '')}
              fallback={userData?.organization?.name?.[0] ?? '?'}
              radius="full"
              size="1"
              ml="-2"
            />
          </Flex>
          <Flex direction="column">
            <Text as="p" wrap="nowrap">
              {userData?.user?.firstname} {userData?.user?.lastname}
            </Text>
            <Text as="p" wrap="nowrap" color="gray" size="2">
              {userData?.organization?.name}
            </Text>
          </Flex>
        </Flex>
      </Card>
      <Card style={styles.card} asChild>
        <Flex direction="column" gap="4" asChild>
          <nav>
            {links.map(
              ({ to, label, show, icon }) =>
                show && (
                  <Button key={to} variant="ghost" style={styles.button} asChild>
                    <NavLink to={to}>
                      {({ isActive }) => (
                        <>
                          {icon}
                          <Text wrap="nowrap" weight={isActive ? 'bold' : 'regular'}>
                            {label}
                          </Text>
                          {/* Mensajes badge: the consultas counter dealers already know, then the
                              whole inbox (consultas + otros) in brackets. Shown while anything is unread. */}
                          {label === 'Mensajes' && unreadMessagesCount > 0 && (
                            <Badge color="red" size="1" style={{ marginLeft: '8px' }}>
                              {unreadConsultasCount} ({unreadMessagesCount})
                            </Badge>
                          )}
                          {/* Show fresh deals count badge for Negocios */}
                          {label === 'Negocios' && unreadDealsCount > 0 && (
                            <Badge color="red" size="1" style={{ marginLeft: '8px' }}>
                              {unreadDealsCount}
                            </Badge>
                          )}
                          {isActive && <ChevronRightIcon />}
                        </>
                      )}
                    </NavLink>
                  </Button>
                )
            )}
          </nav>
        </Flex>
      </Card>
    </Flex>
  )
}

const styles: Record<string, CSSProperties> = {
  card: {
    top: 'var(--space-4)',
    position: 'sticky',
  },
  button: {
    justifyContent: 'start',
    display: 'flex',
    gap: '0.75em',
  },
}
